import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  v2AdminListManualCallBookingCandidatesOptions,
  v2AdminListManualCallBookingCandidatesQueryKey,
  v2AdminListStartupsOptions,
} from '@workspace/api-backend/react-query'
import { Button } from '@workspace/ui/components/button'
import { ConfirmButton } from '@workspace/ui/components/confirm-button'
import { TextInput } from '@workspace/ui/components/text-input'
import { useState } from 'react'
import {
  adminCallCandidatesQueryKey,
  adminLimitsPendingQueryKey,
  adminSeatQueueQueryKey,
  adminStartupMetricsQueryKey,
  adminStuckSendersQueryKey,
  adminWarmIntroCandidatesQueryKey,
  adminWithdrawalRequestsQueryKey,
  outreachApi,
} from '../../../features/outreach/api'
import { heyreach } from '../../../lib/heyreach'

export const Route = createFileRoute('/admin/outreach/')({
  component: AdminOutreachPage,
})

function AdminOutreachPage() {
  return (
    <div className="w-full space-y-10">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Pending Outreach Actions
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Items waiting for admin review — seat promotions, campaign clones, and call confirmations.
          Per-startup access &amp; billing live on each startup's Outreach tab.
        </p>
      </div>
      <StartupMetricsSection />
      <CopyArmsSection />
      <LimitsSetSection />
      <StuckSendersSection />
      <WithdrawalRequestsSection />
      <CallCandidatesSection />
      <WarmIntroCandidatesSection />
      <ManualCallBookingSection />
    </div>
  )
}

function StartupLink({ id, name }: { id: string; name: string }) {
  return (
    <Link
      to="/admin/startups/$id"
      params={{ id }}
      search={{ tab: 'outreach' }}
      className="text-sm font-medium text-orange-500 hover:text-orange-400 hover:underline"
    >
      {name}
    </Link>
  )
}

/**
 * Single HeyReach shortcut for a row: copies the workspace NAME to the clipboard
 * (what ops paste into HeyReach's workspace switcher — the real UI has no
 * workspace-id deep link) AND opens the workspace inbox — the mock locally, real
 * HeyReach in deployed envs.
 */
function WorkspaceActions({
  workspaceId,
  workspaceName,
  openUrl,
}: {
  workspaceId: number | null
  workspaceName: string
  /** Defaults to the workspace inbox; pass a fixed URL (e.g. the workspaces list) to override. */
  openUrl?: string
}) {
  const [copied, setCopied] = useState(false)
  if (workspaceId == null) return null
  return (
    <Button
      size="sm"
      variant="outline"
      title={`Copy workspace name "${workspaceName}" and open HeyReach`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(workspaceName)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        } catch {
          // Clipboard may be blocked (e.g. no focus); still open HeyReach.
        }
        window.open(openUrl ?? heyreach.inboxUrl(workspaceId), '_blank', 'noopener,noreferrer')
      }}
    >
      {copied ? `Copied ${workspaceName} ✓` : 'Open HeyReach ↗'}
    </Button>
  )
}

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
  )
}

function daysStuck(awaitingSenderSince: string | null): string {
  if (!awaitingSenderSince) return '—'
  const ms = Date.now() - new Date(awaitingSenderSince).getTime()
  return `${Math.floor(ms / (24 * 60 * 60 * 1000))}d`
}

/** Same thresholds the admin startup detail uses, so a score reads the same everywhere. */
function prescreenPillClass(score: number | null): string {
  if (score == null) return 'border-border/60 bg-muted/20 text-muted-foreground'
  if (score >= 75) return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
  if (score >= 50) return 'border-amber-500/40 bg-amber-500/10 text-amber-300'
  return 'border-red-500/40 bg-red-500/10 text-red-300'
}

function StatusPill({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>
  return (
    <span className="inline-flex rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-300">
      {status}
    </span>
  )
}

/**
 * Match-pool quality and interaction outcome per startup in outreach.
 *
 * Percentiles rather than mean: the persisted match list is already cut at the
 * rerank threshold and floored at a minimum size, so its mean compresses every
 * startup into the same narrow band. p90 reads the head of the list (which is
 * what outreach works, top-first) and p50 the typical match.
 */
/**
 * Outcomes by copy arm: one row per arm, pooled across startups. An arm covers a lead's whole sequence, so the
 * readout's msg1, msg2 and msg3 cells count the same leads; each lead is counted once, from its msg1 cell.
 *
 * Each startup's "arm-1" is a different piece of text produced by the same
 * prompt, so the Startups column says how many startups a row stands on: a
 * rate from 300 sends of one startup's text is one observation of the arm, not
 * 300.
 *
 * Excluded rows are shown rather than hidden: they are the difference between
 * "all sends" and the sends this comparison is entitled to use.
 */
function CopyArmsSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'outreach', 'copy-arms'],
    queryFn: outreachApi.adminCopyArms,
  })

  if (isLoading) return null
  const cells = data?.cells ?? []
  const exclusions = data?.exclusions

  const rate = (n: number, d: number) => (d === 0 ? '—' : `${Math.round((n / d) * 100)}%`)
  const byArm = new Map<
    string,
    { arm: string; startups: Set<string>; sent: number; accepted: number; replied: number }
  >()
  for (const cell of cells.filter((item) => item.field === 'msg1')) {
    const row = byArm.get(cell.arm) ?? {
      arm: cell.arm,
      startups: new Set<string>(),
      sent: 0,
      accepted: 0,
      replied: 0,
    }
    row.startups.add(cell.startupId)
    row.sent += cell.sent
    row.accepted += cell.accepted
    row.replied += cell.replied
    byArm.set(cell.arm, row)
  }
  // Control first, then the arms in order (arm-2 before arm-10).
  const arms = [...byArm.values()].sort(
    (a, b) =>
      (a.arm === 'control' ? -1 : b.arm === 'control' ? 1 : 0) ||
      a.arm.localeCompare(b.arm, undefined, { numeric: true })
  )

  return (
    <section className="space-y-3">
      <SectionHeader
        title="Outreach copy arms"
        description="How each arm is doing, added up across startups. Sent is leads given the arm; accepted and replied are those leads' outcomes over the whole sequence. Each startup's arm-1 is a different text from the same prompt, so the Startups column is the sample size that matters."
      />

      {exclusions && (
        <p className="text-[11px] text-muted-foreground">
          Excluded: {exclusions.unassigned} pushed before arms existed (should fall to zero as the
          90-day window rolls forward; if it does not, something is pushing without assigning),{' '}
          {exclusions.custom} founder-edited, {exclusions.fallbackModel} produced by a fallback
          model.
        </p>
      )}

      {cells.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No assigned sends yet. Arms reach leads on any campaign once the copy-arms flag is on for
          that startup.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Arm</th>
                <th className="px-3 py-2 text-right">Startups</th>
                <th className="px-3 py-2 text-right">Sent</th>
                <th className="px-3 py-2 text-right">Accepted</th>
                <th className="px-3 py-2 text-right">Replied</th>
              </tr>
            </thead>
            <tbody>
              {arms.map((cell) => (
                <tr key={cell.arm} className="border-t border-border/60">
                  <td className="px-3 py-1.5 font-medium">{cell.arm}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{cell.startups.size}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{cell.sent}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {cell.accepted}{' '}
                    <span className="text-muted-foreground">
                      ({rate(cell.accepted, cell.sent)})
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {cell.replied}{' '}
                    <span className="text-muted-foreground">({rate(cell.replied, cell.sent)})</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function StartupMetricsSection() {
  const { data, isLoading } = useQuery({
    queryKey: adminStartupMetricsQueryKey(),
    queryFn: outreachApi.adminStartupMetrics,
  })
  const { data: seats } = useQuery({
    queryKey: adminSeatQueueQueryKey(),
    queryFn: outreachApi.adminSeatQueue,
  })

  const num = (v: number | null) => (v == null ? '—' : String(v))
  // A channel with no contacts has no progress to report — "0%" would read as
  // "started and stalled", which is a different thing from "not running".
  const pct = (done: number, total: number) =>
    total === 0 ? '—' : `${Math.round((done / total) * 100)}%`

  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <SectionHeader
          title="Startups in outreach"
          description="Match-pool quality and interaction outcomes. Rerank p90/p50 are percentiles of the current match scores — p90 is the head of the list outreach actually works. Contacts, Pending approval and Progress are all split direct/portfolio; Progress counts contacts no longer in flight (sequence exhausted or replied) out of all contacts. A direct count in italics is a planned estimate for startups still awaiting a sender, not yet materialized. Pending approval is contacts waiting on the founder — nothing is pushed until they approve, so a READY startup with everything pending will sit at 0% progress. Engagement matches the founder's “Interactions so far”: accepted connections, replies, calls booked, warm intros and data-room views."
        />
        <div className="shrink-0 rounded-md border border-border/60 bg-card/30 px-3 py-1.5 text-xs text-muted-foreground">
          Seats in use{' '}
          <span className="font-medium text-foreground">{seats?.activeCount ?? 0}</span>
        </div>
      </div>
      <Table
        isLoading={isLoading}
        empty="No startups in outreach yet."
        columns={[
          'Startup',
          'Status',
          'Since',
          'Matches',
          'Rerank p90/p50',
          'Contacts',
          'Pending approval',
          'Progress',
          'Engagement',
        ]}
        rows={
          data?.map((row) => [
            <span key="name" className="flex items-center gap-2">
              <StartupLink id={row.startupId} name={row.startupName} />
              <span
                title="Latest completed council prescreen score"
                className={`inline-flex rounded-md border px-1.5 py-0.5 text-[10px] font-medium tabular-nums ${prescreenPillClass(row.prescreenScore)}`}
              >
                {row.prescreenScore ?? '—'}
              </span>
            </span>,
            <StatusPill key="status" status={row.status} />,
            <span key="since" className="text-xs text-muted-foreground tabular-nums">
              {formatDate(row.since) || '—'}
            </span>,
            <span key="matches" className="text-xs tabular-nums">
              {row.matches}
            </span>,
            <span key="rerank" className="text-xs tabular-nums">
              {num(row.p90RerankScore)}/{num(row.p50RerankScore)}
            </span>,
            <span
              key="contacts"
              title={
                row.directContactsPlanned
                  ? 'direct (planned, awaiting sender) / portfolio'
                  : 'direct / portfolio'
              }
              className="text-xs tabular-nums"
            >
              <span className={row.directContactsPlanned ? 'italic text-muted-foreground' : ''}>
                {row.directContacts}
              </span>
              /{row.portfolioContacts}
            </span>,
            <span
              key="pending"
              title={`Contacts materialized but not yet approved by the founder — direct ${row.directAwaitingApproval}, portfolio ${row.portfolioAwaitingApproval}. Nothing is pushed until a contact is approved.`}
              className={`text-xs tabular-nums ${
                row.directAwaitingApproval + row.portfolioAwaitingApproval > 0
                  ? 'text-amber-500'
                  : 'text-muted-foreground'
              }`}
            >
              {row.directAwaitingApproval}/{row.portfolioAwaitingApproval}
            </span>,
            <span
              key="progress"
              title={`Finished or replied, out of all contacts — direct ${row.directFinished}/${row.directContacts}, portfolio ${row.portfolioFinished}/${row.portfolioContacts}`}
              className="text-xs tabular-nums text-muted-foreground"
            >
              {row.directContactsPlanned ? '—' : pct(row.directFinished, row.directContacts)}/
              {pct(row.portfolioFinished, row.portfolioContacts)}
            </span>,
            <span key="engagement" className="text-xs tabular-nums text-emerald-500">
              {row.engagement}
            </span>,
          ]) ?? []
        }
      />
    </section>
  )
}

function StuckSendersSection() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: adminStuckSendersQueryKey(),
    queryFn: outreachApi.adminStuckSenders,
  })
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})

  const disconnect = useMutation({
    mutationFn: (id: string) => outreachApi.adminResetStuckWorkspace(id),
    onSuccess: (_data, id) => {
      setRowErrors((prev) => ({ ...prev, [id]: '' }))
      queryClient.invalidateQueries({ queryKey: adminStuckSendersQueryKey() })
    },
    onError: (error, id) => setRowErrors((prev) => ({ ...prev, [id]: error.message })),
  })

  return (
    <section className="space-y-3">
      <SectionHeader
        title="Stuck senders"
        description="Startups that started outreach but never connected a LinkedIn sender, for over 2 weeks. Open the workspace and delete it in HeyReach, then Disconnect — it verifies the workspace is actually gone before clearing our record and blocking any recreation."
      />
      <Table
        isLoading={isLoading}
        empty="No senders stuck over 2 weeks."
        columns={['Startup', 'Email', 'Workspace', 'Stuck for', '']}
        rows={
          data?.map((row) => {
            const rowError = rowErrors[row.id]
            return [
              <StartupLink key="name" id={row.startupId} name={row.startupName} />,
              <span key="email" className="text-xs text-muted-foreground">
                {row.invitedEmail}
              </span>,
              <span key="workspace" className="text-xs text-muted-foreground">
                {row.workspaceName}
              </span>,
              <span key="stuck" className="text-xs text-muted-foreground tabular-nums">
                {daysStuck(row.awaitingSenderSince)}
              </span>,
              <div key="action" className="flex flex-col items-end gap-1.5">
                <div className="flex items-center justify-end gap-2">
                  <WorkspaceActions
                    workspaceId={row.workspaceId}
                    workspaceName={row.workspaceName}
                    openUrl={heyreach.workspacesListUrl}
                  />
                  <ConfirmButton
                    confirmLabel="Confirm?"
                    className="w-auto whitespace-nowrap px-4"
                    disabled={disconnect.isPending && disconnect.variables === row.id}
                    onConfirm={() => disconnect.mutate(row.id)}
                  >
                    {disconnect.isPending && disconnect.variables === row.id
                      ? 'Disconnecting…'
                      : 'Disconnect'}
                  </ConfirmButton>
                </div>
                {rowError && <span className="text-[10px] text-destructive">{rowError}</span>}
              </div>,
            ]
          }) ?? []
        }
      />
    </section>
  )
}

function LimitsSetSection() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: adminLimitsPendingQueryKey(),
    queryFn: outreachApi.adminLimitsPending,
  })
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  const markSet = useMutation({
    mutationFn: (id: string) => outreachApi.adminMarkLimitsSet(id),
    onSuccess: (_data, id) => {
      setRowErrors((prev) => ({ ...prev, [id]: '' }))
      queryClient.invalidateQueries({ queryKey: adminLimitsPendingQueryKey() })
    },
    onError: (error, id) => setRowErrors((prev) => ({ ...prev, [id]: error.message })),
  })

  return (
    <section className="space-y-3">
      <SectionHeader
        title="Limits set"
        description="Live campaigns whose HeyReach sending limits haven't been confirmed yet. Set the limits in HeyReach, then mark it done to record the timestamp."
      />
      <Table
        isLoading={isLoading}
        empty="No campaigns waiting on limits."
        columns={['Startup', 'Campaign', 'Created', '']}
        rows={
          data?.map((row) => [
            <StartupLink key="name" id={row.startupId} name={row.startupName} />,
            <span key="campaign" className="text-xs text-muted-foreground tabular-nums">
              {row.campaignId ?? '—'}
            </span>,
            <span key="ts" className="text-xs text-muted-foreground tabular-nums">
              {formatDate(row.createdAt)}
            </span>,
            <div key="action" className="flex flex-col items-end gap-1">
              <div className="flex items-center justify-end gap-2">
                <WorkspaceActions workspaceId={row.workspaceId} workspaceName={row.workspaceName} />
                <Button
                  size="sm"
                  disabled={markSet.isPending && markSet.variables === row.id}
                  onClick={() => markSet.mutate(row.id)}
                >
                  {markSet.isPending && markSet.variables === row.id ? 'Saving…' : 'Limits set'}
                </Button>
              </div>
              {rowErrors[row.id] && (
                <span className="text-[10px] text-destructive">{rowErrors[row.id]}</span>
              )}
            </div>,
          ]) ?? []
        }
      />
    </section>
  )
}

function WithdrawalRequestsSection() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: adminWithdrawalRequestsQueryKey(),
    queryFn: outreachApi.adminWithdrawalRequests,
  })
  const resolve = useMutation({
    mutationFn: (id: string) => outreachApi.adminResolveWithdrawal(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminWithdrawalRequestsQueryKey() }),
  })

  return (
    <section className="space-y-3">
      <SectionHeader
        title="Withdrawal requests"
        description="Founders who asked to withdraw their outreach wallet balance. Refund them in Stripe, then mark the request resolved."
      />
      <Table
        isLoading={isLoading}
        empty="No pending withdrawal requests."
        columns={['Startup', 'Amount', 'Requested', '']}
        rows={
          data?.map((row) => [
            <StartupLink key="name" id={row.startupId} name={row.startupName} />,
            <span key="amount" className="text-xs font-medium text-foreground tabular-nums">
              {(row.amountCents / 100).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}
            </span>,
            <span key="ts" className="text-xs text-muted-foreground tabular-nums">
              {formatDate(row.createdAt)}
            </span>,
            <div key="action" className="flex justify-end">
              <Button
                size="sm"
                variant="outline"
                disabled={resolve.isPending && resolve.variables === row.id}
                onClick={() => resolve.mutate(row.id)}
              >
                {resolve.isPending && resolve.variables === row.id ? 'Resolving…' : 'Mark resolved'}
              </Button>
            </div>,
          ]) ?? []
        }
      />
    </section>
  )
}

function CallCandidatesSection() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: adminCallCandidatesQueryKey(),
    queryFn: outreachApi.adminCallCandidates,
  })
  const decide = useMutation({
    mutationFn: (vars: { messageId: string; decision: 'confirm' | 'dismiss' }) =>
      outreachApi.adminCallDecision(vars.messageId, vars.decision),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminCallCandidatesQueryKey() }),
  })

  return (
    <section className="space-y-3">
      <SectionHeader
        title="Call confirmations"
        description="Replies the LLM flagged as a potential booked call or investor warm intro. Confirm or dismiss to update the lead-routing state."
      />
      <Table
        isLoading={isLoading}
        empty="No call confirmations awaiting review."
        columns={['Startup', 'Investor', 'Contact', 'Summary', '']}
        rows={
          data?.map((row) => [
            <StartupLink key="name" id={row.startupId} name={row.startupName} />,
            row.investorName,
            row.contactName,
            <span key="summary" className="block max-w-md text-xs text-muted-foreground">
              {row.replySummary ?? '—'}
            </span>,
            <div key="actions" className="flex items-center justify-end gap-2">
              <WorkspaceActions workspaceId={row.workspaceId} workspaceName={row.workspaceName} />
              <Button
                size="sm"
                onClick={() => decide.mutate({ messageId: row.messageId, decision: 'confirm' })}
                disabled={decide.isPending && decide.variables?.messageId === row.messageId}
              >
                Confirm call
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => decide.mutate({ messageId: row.messageId, decision: 'dismiss' })}
                disabled={decide.isPending && decide.variables?.messageId === row.messageId}
              >
                Not a call
              </Button>
            </div>,
          ]) ?? []
        }
      />
    </section>
  )
}

function WarmIntroCandidatesSection() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: adminWarmIntroCandidatesQueryKey(),
    queryFn: outreachApi.adminWarmIntroCandidates,
  })
  const decide = useMutation({
    mutationFn: (vars: { messageId: string; startupId: string; decision: 'CONFIRM' | 'DISMISS' }) =>
      outreachApi.adminWarmIntroDecision(vars.messageId, vars.startupId, vars.decision),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: adminWarmIntroCandidatesQueryKey() }),
  })

  return (
    <section className="space-y-3">
      <SectionHeader
        title="Warm intro confirmations"
        description="Portfolio founder replies flagged as a warm intro offer. Confirm or dismiss to update the lead."
      />
      <Table
        isLoading={isLoading}
        empty="No warm intro confirmations awaiting review."
        columns={['Startup', 'Company', 'Fund', 'Founder', 'Summary', '']}
        rows={
          data?.map((row) => [
            <StartupLink key="name" id={row.startupId} name={row.startupName} />,
            row.companyName,
            row.fundName,
            <span key="founder" className="text-sm">
              {row.founderName ?? '—'}
              {row.founderTitle && (
                <span className="ml-1 text-xs text-muted-foreground">· {row.founderTitle}</span>
              )}
            </span>,
            <span key="summary" className="block max-w-sm text-xs text-muted-foreground">
              {row.replySummary ?? '—'}
            </span>,
            <div key="actions" className="flex items-center justify-end gap-2">
              <WorkspaceActions workspaceId={row.workspaceId} workspaceName={row.workspaceName} />
              <Button
                size="sm"
                onClick={() =>
                  decide.mutate({
                    messageId: row.messageId,
                    startupId: row.startupId,
                    decision: 'CONFIRM',
                  })
                }
                disabled={decide.isPending && decide.variables?.messageId === row.messageId}
              >
                Warm intro ✓
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  decide.mutate({
                    messageId: row.messageId,
                    startupId: row.startupId,
                    decision: 'DISMISS',
                  })
                }
                disabled={decide.isPending && decide.variables?.messageId === row.messageId}
              >
                Dismiss
              </Button>
            </div>,
          ]) ?? []
        }
      />
    </section>
  )
}

function ManualCallBookingSection() {
  const queryClient = useQueryClient()
  const [startupSearch, setStartupSearch] = useState('')
  const [selectedStartupId, setSelectedStartupId] = useState<string | null>(null)

  const startupsQuery = useQuery(
    v2AdminListStartupsOptions({
      query: { page: 1, limit: 50, search: startupSearch || undefined },
    })
  )
  const startups = startupsQuery.data?.data?.items ?? []

  const candidatesQuery = useQuery({
    ...v2AdminListManualCallBookingCandidatesOptions({
      query: { startupId: selectedStartupId ?? '' },
    }),
    enabled: !!selectedStartupId,
  })
  const candidatesPayload = candidatesQuery.data
  const candidates = candidatesPayload?.status === 'ok' ? candidatesPayload.data.items : []

  const confirm = useMutation({
    mutationFn: (messageId: string) => outreachApi.adminCallDecision(messageId, 'confirm'),
    onSuccess: () => {
      if (!selectedStartupId) return
      queryClient.invalidateQueries({
        queryKey: v2AdminListManualCallBookingCandidatesQueryKey({
          query: { startupId: selectedStartupId },
        }),
      })
    },
  })

  return (
    <section className="space-y-3">
      <SectionHeader
        title="Manual call booking"
        description="Heard about a booked call out-of-band (Slack, calendar)? Pick the startup and investor we contacted via outreach and mark it confirmed."
      />

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label
            htmlFor="manual-call-startup-search"
            className="text-[11px] uppercase tracking-wider text-muted-foreground"
          >
            Startup
          </label>
          <TextInput
            id="manual-call-startup-search"
            inputSize="sm"
            className="mt-1.5"
            value={startupSearch}
            onChange={(e) => setStartupSearch(e.target.value)}
            placeholder="Search startups…"
          />
          <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-border/40 bg-background/30">
            {startupsQuery.isLoading ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">Loading…</div>
            ) : startups.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">No startups match.</div>
            ) : (
              startups.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSelectedStartupId(s.id)}
                  className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-foreground/5 ${
                    s.id === selectedStartupId
                      ? 'bg-foreground/10 text-foreground'
                      : 'text-muted-foreground'
                  }`}
                >
                  <span className="font-medium text-foreground">{s.name}</span>
                  {s.founder?.email ? (
                    <span className="ml-2 text-muted-foreground">{s.founder.email}</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>

        <div>
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Investors we've contacted
          </span>
          {!selectedStartupId ? (
            <div className="mt-1.5 rounded-md border border-dashed border-border/60 px-3 py-6 text-center text-xs text-muted-foreground">
              Pick a startup on the left.
            </div>
          ) : candidatesQuery.isLoading ? (
            <div className="mt-1.5 rounded-md border border-border/60 px-3 py-6 text-center text-xs text-muted-foreground">
              Loading…
            </div>
          ) : candidates.length === 0 ? (
            <div className="mt-1.5 rounded-md border border-border/60 px-3 py-6 text-center text-xs text-muted-foreground">
              No eligible messages — every pushed investor either has a confirmed call or hasn't
              been contacted yet.
            </div>
          ) : (
            <div className="mt-1.5 max-h-64 overflow-y-auto rounded-md border border-border/40 bg-background/30">
              {candidates.map((c) => (
                <div
                  key={c.messageId}
                  className="flex items-center justify-between gap-3 border-b border-border/30 px-3 py-2 last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-foreground">
                      {c.investorName}
                    </div>
                    <div className="truncate text-[10px] text-muted-foreground">
                      {c.contactName} · {c.contactTitle || '—'}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => confirm.mutate(c.messageId)}
                    disabled={confirm.isPending && confirm.variables === c.messageId}
                  >
                    {confirm.isPending && confirm.variables === c.messageId
                      ? 'Confirming…'
                      : 'Confirm call'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function Table({
  columns,
  rows,
  isLoading,
  empty,
}: {
  columns: string[]
  rows: React.ReactNode[][]
  isLoading: boolean
  empty: string
}) {
  if (isLoading) {
    return (
      <div className="rounded-xl border border-border/60 bg-card/30 px-5 py-8 text-center text-sm text-muted-foreground">
        Loading…
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border/60 bg-card/30 px-5 py-8 text-center text-sm text-muted-foreground">
        {empty}
      </div>
    )
  }
  return (
    <div className="overflow-hidden rounded-xl border border-border/60">
      <table className="w-full text-sm">
        <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
          <tr>
            {columns.map((c) => (
              <th key={c} className="px-4 py-2 text-left font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: row identity not available at this layer
            <tr key={i} className="border-t border-border/40">
              {row.map((cell, j) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: column position is the identity
                <td key={j} className="px-4 py-3 align-middle">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
